import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { NotificationProvider } from './contexts/NotificationContext';
import { searchLoadingState } from './hooks/useSearch';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingOverlay from './components/LoadingOverlay';

// Import components
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SubprocessorsPage from './pages/SubprocessorsPage';
import DPAPage from './pages/DPAPage';
import DashboardPage from './pages/DashboardPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import QBRPage from './pages/QBRPage';
import QBRDetailPage from './pages/QBRDetailPage';
import QBRTemplatesPage from './pages/QBRTemplatesPage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import InsightsQueryPage from './pages/InsightsQueryPage';
import AccountRadarPage from './pages/AccountRadarPage';
import EdytChatPage from './pages/EdytChatPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ProgressTrackerPage from './pages/ProgressTrackerPage';
import SearchActionsPage from './pages/SearchActionsPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import AdminRoute from './components/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  console.log('[App] Starting Edyt SaaS Platform');
  const [loadingState, setLoadingState] = useState<{ isLoading: boolean; requestType?: any; query?: string }>({ 
    isLoading: false, 
    requestType: undefined, 
    query: undefined 
  });
  
  useEffect(() => {
    // Subscribe to global search loading state
    const unsubscribe = searchLoadingState.subscribe((state) => {
      setLoadingState(state);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  
  return (
    <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <LoadingOverlay 
              isVisible={loadingState.isLoading} 
              requestType={loadingState.requestType || 'rag_query'}
              query={loadingState.query}
            />
            <AuthProvider>
              <NotificationProvider>
                <Router>
                <div className="App">
                  <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/subprocessors" element={<SubprocessorsPage />} />
                  <Route path="/dpa" element={<DPAPage />} />
                  
                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <DashboardPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/integrations"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <IntegrationsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <CustomersPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/customers/:customerId"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <CustomerDetailPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/customers/:customerId/qbrs"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <QBRPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/customers/:customerId/qbrs/:reportId"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <QBRDetailPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/qbrs"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <QBRPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/qbr-templates"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <QBRTemplatesPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <SettingsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <NotificationsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/insights/query"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <InsightsQueryPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/insights/radar"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <AccountRadarPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <EdytChatPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <AnalyticsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/progress-tracker"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <ProgressTrackerPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/search-actions"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <SearchActionsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/insights/cached/:queryId"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <InsightsQueryPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/settings/billing"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <BillingPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <DashboardLayout>
                            <AdminPage />
                          </DashboardLayout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
              </NotificationProvider>
            </AuthProvider>
          </ThemeProvider>
          
          {/* React Query DevTools - only in development */}
          {process.env.NODE_ENV === 'development' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </ErrorBoundary>
    );
}

export default App;